import PrintableDocument from './PrintableDocument';

interface SoinItem {
  date: string;
  soin: string;
  tarif: string;
}

interface ConstantesVitales {
  taille: string;
  poids: string;
  tension_arterielle: string;
  temperature: string;
  libelle: string;
}

interface PrintableBonLaboratoireProps {
  patientName: string;
  practitionerName: string;
  practitionerTitle?: string;
  date: string;
  analyses: string[];
  description?: string;
  observations?: string;
  urgences: boolean;
  soins: SoinItem[];
  constantes: ConstantesVitales;
  patientAge: string;
  patientTelephone: string;
  patientProfession: string;
  patientAssure: boolean;
  medecinTraitant: string;
  antecedentsMedicaux?: string;
  verificationUrl?: string;
  onReady?: () => void;
}

export default function PrintableBonLaboratoire({
  patientName,
  practitionerName,
  practitionerTitle,
  date,
  analyses,
  description,
  observations,
  urgences,
  soins,
  constantes,
  patientAge,
  patientTelephone,
  patientProfession,
  patientAssure,
  medecinTraitant,
  antecedentsMedicaux,
  verificationUrl,
  onReady,
}: PrintableBonLaboratoireProps) {
  const soinsFiltres = soins.filter(s => s.date.trim() || s.soin.trim() || s.tarif.trim());
  const totalTarifs = soinsFiltres.reduce((sum, s) => sum + (parseInt(s.tarif, 10) || 0), 0);

  const hasConstantes = constantes.taille.trim() || constantes.poids.trim() || constantes.tension_arterielle.trim() || constantes.temperature.trim() || constantes.libelle.trim();

  return (
    <PrintableDocument
      documentTitle="Bon de Laboratoire"
      patientName={patientName}
      practitionerName={practitionerName}
      practitionerTitle={practitionerTitle}
      date={date}
      verificationUrl={verificationUrl}
      onReady={onReady}
    >
      {/* Informations patient */}
      <div style={{ marginBottom: '4mm' }}>
        <div className="print-row">
          <div className="print-col">
            <span className="print-label">Âge :</span> {patientAge}
          </div>
          {patientTelephone && (
            <div className="print-col">
              <span className="print-label">Tél :</span> {patientTelephone}
            </div>
          )}
        </div>
        {patientProfession && (
          <div style={{ marginTop: '1mm' }}>
            <span className="print-label">Profession :</span> {patientProfession}
          </div>
        )}
        <div style={{ marginTop: '1mm' }}>
          <span className="print-label">Assuré :</span> {patientAssure ? 'Oui' : 'Non'}
        </div>
        {medecinTraitant && (
          <div style={{ marginTop: '1mm' }}>
            <span className="print-label">Médecin traitant :</span> {medecinTraitant}
          </div>
        )}
      </div>

      {/* Constantes vitales */}
      {hasConstantes && (
        <>
          <div className="print-section-title">
            <i className="ri-heart-pulse-fill" style={{ marginRight: '6px', fontSize: '14px' }}></i>
            Constantes vitales du jour
          </div>
          <table className="print-table" style={{ marginBottom: '4mm' }}>
            <thead>
              <tr>
                <th>Taille</th>
                <th>Poids</th>
                <th>T.A.</th>
                <th>Température</th>
                <th>Libellé</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{constantes.taille ? `${constantes.taille} cm` : '—'}</td>
                <td>{constantes.poids ? `${constantes.poids} kg` : '—'}</td>
                <td>{constantes.tension_arterielle ? `${constantes.tension_arterielle} mmHg` : '—'}</td>
                <td>{constantes.temperature ? `${constantes.temperature} °C` : '—'}</td>
                <td>{constantes.libelle || '—'}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      {/* Antécédents médicaux */}
      {antecedentsMedicaux && (
        <>
          <div className="print-section-title">
            <i className="ri-file-history-fill" style={{ marginRight: '6px', fontSize: '14px' }}></i>
            Antécédents médicaux
          </div>
          <div className="print-text" style={{ marginBottom: '4mm' }}>{antecedentsMedicaux}</div>
        </>
      )}

      {urgences && (
        <div className="print-urgence">
          <i className="ri-alert-fill" style={{ marginRight: '6px' }}></i>
          ANALYSE URGENTE — À traiter en priorité
        </div>
      )}

      {/* Analyses demandées */}
      <div className="print-section-title">
        <i className="ri-flask-fill" style={{ marginRight: '6px', fontSize: '14px' }}></i>
        Analyses demandées
      </div>
      <div style={{ marginBottom: '4mm' }}>
        {analyses.map((a, i) => (
          <span key={i} className="print-badge">{a}</span>
        ))}
      </div>

      {/* Observations cliniques */}
      {observations && (
        <>
          <div className="print-section-title">Observations cliniques</div>
          <div className="print-text" style={{ marginBottom: '4mm' }}>{observations}</div>
        </>
      )}

      {/* Description */}
      {description && (
        <>
          <div className="print-section-title">Description détaillée</div>
          <div className="print-text" style={{ marginBottom: '4mm' }}>{description}</div>
        </>
      )}

      {/* Soins associés */}
      {soinsFiltres.length > 0 && (
        <>
          <div className="print-section-title">
            <i className="ri-hospital-fill" style={{ marginRight: '6px', fontSize: '14px' }}></i>
            Soins associés
          </div>
          <table className="print-table">
            <thead>
              <tr>
                <th style={{ width: '90px' }}>Date</th>
                <th>Soin / Acte</th>
                <th style={{ width: '100px', textAlign: 'right' }}>Tarif (FCFA)</th>
              </tr>
            </thead>
            <tbody>
              {soinsFiltres.map((s, i) => (
                <tr key={i}>
                  <td>{s.date ? new Date(s.date).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>{s.soin || '—'}</td>
                  <td style={{ textAlign: 'right' }}>
                    {s.tarif ? parseInt(s.tarif, 10).toLocaleString('fr-FR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
            {totalTarifs > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={2} style={{ textAlign: 'right' }}>Total :</td>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{totalTarifs.toLocaleString('fr-FR')} FCFA</strong>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </>
      )}

      {/* Signature */}
      <div style={{ marginTop: '10mm', textAlign: 'right' }}>
        <div style={{ fontSize: '10pt', color: '#666' }}>{practitionerName}</div>
        {practitionerTitle && <div style={{ fontSize: '9pt', color: '#999' }}>{practitionerTitle}</div>}
      </div>
    </PrintableDocument>
  );
}